import { useCalcPrice } from "@/talons";
import { CartItems } from "./CartContext";
import { HandlePrices, calcPrice } from "@/shared/calculatePrices";
import { PriceProductProps, TaxSubstitutionProps } from "@/types";
import { useMemo, useContext, useCallback, createContext, useRef } from "react";
import { useTables, useGlobals, useCart, useDiscountFlex, useDiscountAmountFlex } from "@/hooks";
import { useSelectedClientScope } from "./useSelectedClientScope";
import { useCorporateFlags } from "./CorporateFlagsContext";

interface CartToTalsProps {
  cartTotals: {
    flex: any;
    total: number;
    subtotal: number;
    st_price: number;
  };
  fetchCorrectPrice: (
    price: PriceProductProps[],
    option_uid: string,
    paymentCondition: number
  ) => PriceProductProps;
  fetchTotalPriceByPaymentCode: (code: number) => number;
  listaTributacao: TaxSubstitutionProps[];
}

export const CartTotalsContext = createContext({} as CartToTalsProps);

const CartTotalsProvider = ({ children }: React.PropsWithChildren) => {
  const { tables } = useTables();
  const { getPrices, findPrice } = useCalcPrice();
  const products = tables.current.dicionario_produtos;
  const listaTributacao = tables.current.cod_regime_tributario;

  const { currentCartDetails } = useCart();
  const { paymentCode } = useGlobals();
  const { selectedBrandPair } = useCorporateFlags();
  const { getDiscountProduct } = useDiscountFlex();
  const { discountAmountClient } = useDiscountAmountFlex();
  const debugTotalsByPaymentRef = useRef<Record<number, {
    total: number;
    generatedAt: string;
    itemPrices: Record<string, number | null>;
  }>>({});

  const selectedClientScope = useSelectedClientScope();
  const shouldDebugPaymentTotals = false;

  const fetchCorrectPrice = useCallback(
    (
      price: PriceProductProps[],
      option_uid: string,
      paymentCondition: number
    ) => {
      return findPrice(price, option_uid, paymentCondition);
    },
    [findPrice]
  );

  const fetchTotalPriceByPaymentCode = useCallback(
    (code: number) => {
      if (!selectedClientScope) return 0;
      if (
        currentCartDetails?.items?.length > 0 &&
        code
      ) {
        const debugItemPricesByKey = {} as Record<string, number | null>;
        if (shouldDebugPaymentTotals) {
          console.log('[CartTotalsContext][cart-total-debug][start]', {
            paymentConditionCode: code,
            selectedClientScope,
            selectedBrandPair,
            paymentCodeGlobal: paymentCode,
            cartDetailsKeyInfo: {
              itemsInCart: currentCartDetails?.items?.length || 0,
              items: currentCartDetails?.items || [],
            },
          });
        }

        const totals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
          if (!products?.[item?.sku]) return acc;
          const prices = getPrices(products?.[item?.sku]);
          
          if (!!prices) {
            const [packing, qtd] = item?.option_uid?.split("_");

            const flexEntry = getDiscountProduct({
              package: packing,
              productId: item?.sku,
              quantity: Number(qtd),
            });
            const percentualFlex = Number(flexEntry?.percentual ?? 0);
            const rawDiscount = flexEntry?.discountPrice?.[`${code}`];

            const price = findPrice(prices, item?.option_uid, code);
            const candidatePricesForCondition = prices.filter((p) => Number(p?.NROCONDICAOPAGTO) === Number(code));

            let finalprice: number | undefined;
            let branchUsed = 'none';
            if (rawDiscount !== undefined && rawDiscount !== null) {
              finalprice = Number(rawDiscount);
              branchUsed = 'rawDiscount';
            } else if (
              percentualFlex !== 0 &&
              price &&
              typeof price.VLR_PRECO === "number" &&
              listaTributacao?.length
            ) {
              const adjustedRow: PriceProductProps = {
                ...price,
                VLR_PRECO: price.VLR_PRECO + (price.VLR_PRECO / 100) * percentualFlex,
              };
              const calculated = calcPrice(selectedClientScope, [adjustedRow], listaTributacao);
              finalprice = calculated[0]?.VLR_FINAL;
              branchUsed = 'calcPriceWithFlexPercentual';
            }
            if (finalprice === undefined || finalprice === null || Number.isNaN(finalprice)) {
              finalprice = price?.VLR_FINAL || price?.VLR_PRECO;
              branchUsed = 'fallbackMatchedPrice';
            }

            if (finalprice) {
              const fullprice = finalprice * item?.quantity;
              if (shouldDebugPaymentTotals) {
                console.log('[CartTotalsContext][cart-total-debug][item]', {
                  paymentConditionCode: code,
                  item,
                  selectedClientScope,
                  optionUidParsed: {
                    packing,
                    qtd,
                  },
                  productFromDictionary: products?.[item?.sku],
                  pricesInputLength: prices?.length || 0,
                  pricesInput: prices,
                  candidatePricesForConditionLength: candidatePricesForCondition.length,
                  candidatePricesForCondition,
                  matchedPrice: price,
                  matchedPricePricingFactors: {
                    perAcrFinanceiro: price?.PERACRFINANCEIRO,
                    perAcrEntrega: price?.PERACRENTREGA,
                    perAcrDescFornecedor: price?.PERACRESCDESC,
                    percCliente: selectedClientScope?.PERC_CLIENTE,
                    nrotabvenda: price?.NROTABVENDA,
                  },
                  matchedPriceFiscalFactors: {
                    codTributacaoProduto: price?.COD_TRIBUTACAO_PRODUTO,
                    destacaSt: price?.DESTACA_ST,
                    stVlrFinal: price?.ST_VLR_FINAL,
                    stVlrFinalCart: price?.ST_VLR_FINAL_CART,
                    vlrPauta: price?.VLR_PAUTA,
                    codRegimeTribCliente: selectedClientScope?.COD_REGIME_TRIB,
                  },
                  flexEntry,
                  percentualFlex,
                  rawDiscount,
                  branchUsed,
                  finalprice,
                  fullprice,
                  runningTotalBeforeAdd: acc,
                  runningTotalAfterAdd: acc + fullprice,
                });
              }
              debugItemPricesByKey[`${item?.sku}-${item?.option_uid}`] = finalprice;
              if (fullprice) return (acc += fullprice);
            }

            if (shouldDebugPaymentTotals) {
              if (!candidatePricesForCondition.length) {
                console.warn('[CartTotalsContext][cart-total-debug][warning-no-candidates-for-condition]', {
                  paymentConditionCode: code,
                  item,
                  optionUidParsed: { packing, qtd },
                  selectedClientScope,
                });
              }

              if (!price?.NROCONDICAOPAGTO || Number(price?.NROCONDICAOPAGTO) !== Number(code)) {
                console.warn('[CartTotalsContext][cart-total-debug][warning-no-matched-price]', {
                  paymentConditionCode: code,
                  item,
                  optionUidParsed: { packing, qtd },
                  candidatePricesForConditionLength: candidatePricesForCondition.length,
                  selectedClientScope,
                });
              }

              console.log('[CartTotalsContext][cart-total-debug][item-no-add]', {
                paymentConditionCode: code,
                item,
                selectedClientScope,
                pricesInputLength: prices?.length || 0,
                pricesInput: prices,
                candidatePricesForConditionLength: candidatePricesForCondition.length,
                candidatePricesForCondition,
                matchedPrice: price,
                flexEntry,
                percentualFlex,
                rawDiscount,
                branchUsed,
                finalprice,
                fullpriceComputed: finalprice ? finalprice * item?.quantity : null,
              });
            }
            debugItemPricesByKey[`${item?.sku}-${item?.option_uid}`] = finalprice ?? null;
          }
          return acc;
        }, 0);

        if (shouldDebugPaymentTotals) {
          debugTotalsByPaymentRef.current[code] = {
            total: totals || 0,
            generatedAt: new Date().toISOString(),
            itemPrices: debugItemPricesByKey,
          };

          const paymentTotalsSnapshot = Object.entries(debugTotalsByPaymentRef.current).map(([paymentCond, payload]) => ({
            paymentConditionCode: Number(paymentCond),
            total: payload.total,
            generatedAt: payload.generatedAt,
            itemPrices: payload.itemPrices,
          }));
          const totalSet = new Set(paymentTotalsSnapshot.map((entry) => Number(entry.total || 0).toFixed(2)));
          const allTotalsEqual = paymentTotalsSnapshot.length > 1 && totalSet.size === 1;

          if (allTotalsEqual) {
            console.warn('[CartTotalsContext][cart-total-debug][warning-all-payment-totals-equal]', {
              codCliente: selectedClientScope?.COD_CLIENTE,
              codLoja: selectedClientScope?.COD_LOJA,
              codSegmentos:
                selectedClientScope?.COD_SEGMENTO?.map((s: any) => s?.cod_segmento ?? s?.COD_SEGMENTO ?? s) || [],
              codRegimeTribCliente: selectedClientScope?.COD_REGIME_TRIB,
              paymentTotalsSnapshot,
            });
          }
        }

        if (shouldDebugPaymentTotals) {
          console.log('[CartTotalsContext][cart-total-debug][end]', {
            codCliente: selectedClientScope?.COD_CLIENTE,
            codLoja: selectedClientScope?.COD_LOJA,
            codSegmentos:
              selectedClientScope?.COD_SEGMENTO?.map((s: any) => s?.cod_segmento ?? s?.COD_SEGMENTO ?? s) || [],
            codRegimeTribCliente: selectedClientScope?.COD_REGIME_TRIB,
            paymentConditionCode: code,
            itemsInCart: currentCartDetails?.items?.length || 0,
            total: totals,
          });
        }
        return totals;
      }
      return 0;
    },
    [currentCartDetails, products, getDiscountProduct, selectedClientScope, listaTributacao, getPrices]
  );

  const handleCalcStTotals = useCallback((code: number) => {
    if (!selectedClientScope) return 0;
    if (currentCartDetails?.items?.length > 0 && code) {
      const stTotals = currentCartDetails?.items?.reduce((acc: number, item: CartItems) => {
        if (!products?.[item?.sku]) return acc;
        const prices = getPrices(products?.[item?.sku]);

        if (!!prices.length) {
          const [packing, qtd] = item?.option_uid?.split("_");

          const price = findPrice(prices, item?.option_uid, code);

          if (price?.DESTACA_ST === 'S' && selectedClientScope?.COD_LOJA === 743 && listaTributacao?.length && price?.VLR_PRECO_CALCULADO) {
            const tributacao = HandlePrices.getTributacao(selectedClientScope, price?.COD_TRIBUTACAO_PRODUTO, listaTributacao);

            const precoSt = HandlePrices.CalculaST(tributacao, price?.VLR_PRECO_CALCULADO * item?.quantity, price?.VLR_PAUTA);

            if (precoSt) {
              return acc += precoSt;
            }
            return acc;
          }
          return acc;
        }
        return acc;
      }, 0);
      return stTotals;
    }
    return 0;
  }, [currentCartDetails, products, listaTributacao, selectedClientScope, getPrices]);

  const cartTotals = useMemo(() => {
    const total = fetchTotalPriceByPaymentCode(paymentCode);
    const stTotals = handleCalcStTotals(paymentCode);
    return {
      total,
      subtotal: total - stTotals,
      st_price: stTotals,
      flex: discountAmountClient || 0,
    }
  }, [paymentCode, fetchTotalPriceByPaymentCode, handleCalcStTotals]);

  return (
    <CartTotalsContext.Provider value={{
      cartTotals,
      fetchCorrectPrice,
      fetchTotalPriceByPaymentCode,
      listaTributacao,
    }}>
      {children}
    </CartTotalsContext.Provider>
  );
};

const useCartTotals = () => {
  const context = useContext(CartTotalsContext);

  if (!context) {
    throw new Error(
      "o context useCartTotals só pode ser utilizado dentro de seu escopo. Erro ao usar hook useCartTotals"
    );
  }
  return context;
};

export { CartTotalsProvider, useCartTotals };
